import { Controller } from "react-hook-form";
import { z } from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

export const optStr = z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), z.string().optional());
export const optNum = (msg) =>
  z.preprocess(
    (v) => (v === "" || v == null ? undefined : Number(v)),
    z.number({ invalid_type_error: msg || "Must be a number" }).positive(msg || "Must be a positive number").optional()
  );
export const optInt = z.preprocess(
  (v) => (v === "" || v == null ? undefined : Number(v)),
  z.number().int("Must be a whole number").nonnegative().optional()
);
export const phone = z.string().regex(/^(\+91[\s-]?)?[6-9]\d{9}$/, "Enter a valid 10-digit Indian phone number");
export const optPhone = z.preprocess((v) => (typeof v === "string" && v.trim() === "" ? undefined : v), phone.optional());
export const optEmail = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? undefined : v),
  z.string().email("Invalid email").optional()
);
export const commaArray = z.preprocess(
  (v) => (typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : v ?? []),
  z.array(z.string())
);

export const Section = ({ title, children }) => (
  <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8">
    <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-6 pb-3 border-b border-slate-100">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">{children}</div>
  </section>
);

export const F = ({ form, name, label, required, type = "text", placeholder, span, prefix = "fld" }) => (
  <div className={`space-y-1.5 ${span || ""}`}>
    <Label className="text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    <Input type={type} placeholder={placeholder} data-testid={`${prefix}-${name}`} className="rounded-lg" {...form.register(name)} />
    {form.formState.errors[name] && <p className="text-sm text-red-600">{form.formState.errors[name].message}</p>}
  </div>
);

export const S = ({ form, name, label, options, required, span, onValueChange, prefix = "fld" }) => (
  <div className={`space-y-1.5 ${span || ""}`}>
    <Label className="text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</Label>
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Select
          value={field.value || ""}
          onValueChange={(v) => {
            field.onChange(v === "__none__" ? "" : v);
            onValueChange?.(v);
          }}
        >
          <SelectTrigger data-testid={`${prefix}-${name}`} className="rounded-lg"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {options.map((o) => {
              const value = typeof o === "string" ? o : o.value;
              const label2 = typeof o === "string" ? o : o.label;
              return <SelectItem key={value} value={value}>{label2}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      )}
    />
    {form.formState.errors[name] && <p className="text-sm text-red-600">{form.formState.errors[name].message}</p>}
  </div>
);

export const T = ({ form, name, label, span, prefix = "fld" }) => (
  <div className={`space-y-1.5 ${span || "sm:col-span-2 lg:col-span-3"}`}>
    <Label className="text-slate-700">{label}</Label>
    <Textarea rows={2} data-testid={`${prefix}-${name}`} className="rounded-lg" {...form.register(name)} />
  </div>
);

export const CB = ({ form, name, label, prefix = "fld" }) => (
  <div className="flex items-center gap-2 pt-7">
    <Controller
      name={name}
      control={form.control}
      render={({ field }) => (
        <Checkbox checked={!!field.value} onCheckedChange={field.onChange} data-testid={`${prefix}-${name}`} />
      )}
    />
    <Label className="text-slate-700">{label}</Label>
  </div>
);
